import React, { useCallback, useMemo, useState } from "react";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Box, Stack, Typography } from "@mui/material";
import { CharacteristicDef } from "store/types";

import { ContactState, LocalCharacteristicData, useDealCreateContext } from "../context/DealCreateContext";
import { CharacteristicField } from "./CharacteristicField";
import { DealContactFields } from "./DealContactFields";
import { MultipleCharacteristicField } from "./MultipleCharacteristicField";
import { NavigationButtons } from "./NavigationButtons";

const getTabIcon = (tab: string, isActive: boolean) => {
  const color = isActive ? "#071f35" : "#496682";
  const sx = { fontSize: 16, color };
  switch (tab) {
    case "General":
      return <InfoOutlinedIcon sx={sx} />;
    case "Legal":
      return <ArticleOutlinedIcon sx={sx} />;
    case "Financial":
      return <AssessmentOutlinedIcon sx={sx} />;
    case "Contact":
      return <PersonOutlineIcon sx={sx} />;
    default:
      return null;
  }
};

export const CharacteristicsScreen: React.FC = () => {
  const {
    state,
    setState,
    goToScreen,
    goBack,
    submitting,
    characteristics: allCharacteristics,
    categories,
    stages
  } = useDealCreateContext();

  const [activeTab, setActiveTab] = useState(0);

  const requiredCharIds = useMemo(() => {
    const selectedStage = stages.find((s) => s.id === state.stageId);
    if (!selectedStage) return new Set<number>();
    const statusId = selectedStage.status_id;
    return new Set(allCharacteristics.filter((c) => c.required_to_status_ids?.includes(statusId)).map((c) => c.id));
  }, [stages, state.stageId, allCharacteristics]);

  const characteristics = useMemo<CharacteristicDef[]>(
    () =>
      allCharacteristics
        .filter((c) => c.category_name !== "Asset Report")
        .map((item) => ({
          id: item.id,
          name: item.name,
          category_name: item.category_name,
          data_type: item.data_type,
          is_multiple: item.is_multiple,
          list_options: item.list_options,
          required: requiredCharIds.has(item.id)
        })),
    [allCharacteristics, requiredCharIds]
  );

  // Tabs from API categories + Contact
  const tabs = useMemo(() => {
    const cats = categories.filter((c) => c !== "Contact");
    return [...cats, "Contact"];
  }, [categories]);

  const currentTab = tabs[activeTab] || tabs[0];

  // Track which tabs have at least one filled value
  const filledTabs = useMemo(() => {
    const filled = new Set<string>();

    // Check characteristic categories
    for (const cd of state.characteristicsData) {
      if (!cd.value) continue;
      const def = characteristics.find((c) => c.id === cd.characteristic_id);
      if (def) filled.add(def.category_name);
    }

    // Check Contact tab
    const hasContact = Object.values(state.contact).some((v) => typeof v === "string" && v.trim());
    if (hasContact) filled.add("Contact");

    return filled;
  }, [state.characteristicsData, state.contact, characteristics]);

  // Characteristics for current category
  const currentCharacteristics = useMemo(() => {
    if (!currentTab || currentTab === "Contact") return [];
    return characteristics
      .filter((c) => c.category_name === currentTab)
      .sort((a, b) => {
        // Required characteristics first
        if (a.required && !b.required) return -1;
        if (!a.required && b.required) return 1;
        return 0;
      });
  }, [currentTab, characteristics]);

  // ---- Single characteristic value management ----

  const getCharValue = useCallback(
    (charId: number): string => {
      return state.characteristicsData.find((cd) => cd.characteristic_id === charId)?.value || "";
    },
    [state.characteristicsData]
  );

  const setCharValue = useCallback(
    (charId: number, value: string) => {
      setState((prev) => {
        // Delete: remove entry when value is cleared
        if (!value) {
          return {
            ...prev,
            characteristicsData: prev.characteristicsData.filter((cd) => cd.characteristic_id !== charId)
          };
        }
        // Update: modify existing entry
        const existing = prev.characteristicsData.find((cd) => cd.characteristic_id === charId);
        if (existing) {
          return {
            ...prev,
            characteristicsData: prev.characteristicsData.map((cd) =>
              cd.characteristic_id === charId ? { ...cd, value } : cd
            )
          };
        }
        // Add: create new entry
        return {
          ...prev,
          characteristicsData: [
            ...prev.characteristicsData,
            {
              localId: `char_${charId}`,
              characteristic_id: charId,
              value,
              currency: null,
              percentage: 100,
              is_default: true
            }
          ]
        };
      });
    },
    [setState]
  );

  const getCharCurrency = useCallback(
    (charId: number): string | null => {
      return state.characteristicsData.find((cd) => cd.characteristic_id === charId)?.currency ?? null;
    },
    [state.characteristicsData]
  );

  const setCharCurrency = useCallback(
    (charId: number, currency: string | null) => {
      setState((prev) => {
        const existing = prev.characteristicsData.find((cd) => cd.characteristic_id === charId);
        if (existing) {
          return {
            ...prev,
            characteristicsData: prev.characteristicsData.map((cd) =>
              cd.characteristic_id === charId ? { ...cd, currency } : cd
            )
          };
        }
        // Create entry with currency but no value yet
        return {
          ...prev,
          characteristicsData: [
            ...prev.characteristicsData,
            {
              localId: `char_${charId}`,
              characteristic_id: charId,
              value: "",
              currency,
              percentage: 100,
              is_default: true
            }
          ]
        };
      });
    },
    [setState]
  );

  // ---- Multiple characteristic entries management ----

  const getMultipleEntries = useCallback(
    (charId: number): LocalCharacteristicData[] => {
      return state.characteristicsData.filter((cd) => cd.characteristic_id === charId);
    },
    [state.characteristicsData]
  );

  const setMultipleEntries = useCallback(
    (charId: number, entries: LocalCharacteristicData[]) => {
      setState((prev) => ({
        ...prev,
        characteristicsData: [...prev.characteristicsData.filter((cd) => cd.characteristic_id !== charId), ...entries]
      }));
    },
    [setState]
  );

  // ---- Contact fields ----

  const setContactField = useCallback(
    (field: keyof ContactState, value: string) => {
      setState((prev) => ({
        ...prev,
        contact: { ...prev.contact, [field]: value }
      }));
    },
    [setState]
  );

  return (
    <>
      <Stack sx={{ padding: "32px", gap: "20px", flexGrow: 1, overflowY: "auto" }}>
        {/* Tabs */}
        <Stack sx={{ borderBottom: "1px solid #dbe8f5" }}>
          <Stack direction="row" alignItems="stretch" sx={{ gap: "16px" }}>
            {tabs.map((tab, index) => (
              <React.Fragment key={tab}>
                {!!index && <Box sx={{ width: "1px", bgcolor: "#dbe8f5", my: "6px" }} />}
                <Stack
                  direction="row"
                  alignItems="center"
                  onClick={() => setActiveTab(index)}
                  sx={{
                    gap: "5px",
                    pb: "6px",
                    pt: "2px",
                    cursor: "pointer",
                    borderBottom: activeTab === index ? "2px solid #071f35" : "2px solid transparent",
                    mb: "-1px"
                  }}
                >
                  {getTabIcon(tab, activeTab === index)}
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "16px",
                      color: activeTab === index ? "#071f35" : "#496682",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {tab}
                  </Typography>
                  {filledTabs.has(tab) && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#26a6ae",
                        flexShrink: 0,
                        mb: "10px"
                      }}
                    />
                  )}
                </Stack>
              </React.Fragment>
            ))}
          </Stack>
        </Stack>

        {/* Tab Content */}
        {currentTab === "Contact" ? (
          <DealContactFields contact={state.contact} onFieldChange={setContactField} />
        ) : (
          <Stack direction="row" flexWrap="wrap" gap="20px">
            {currentCharacteristics.map((char) => {
              const charValue = getCharValue(char.id);
              const showError = char.required && !charValue;
              const charCurrency = char.data_type === "currency" ? getCharCurrency(char.id) : undefined;
              const showCurrencyError = char.required && char.data_type === "currency" && !charCurrency;
              return (
                <Box key={char.id} sx={{ flex: "0 1 calc(50% - 10px)", minWidth: 0 }}>
                  {char.is_multiple ? (
                    <MultipleCharacteristicField
                      characteristic={char}
                      entries={getMultipleEntries(char.id)}
                      onEntriesChange={(entries) => setMultipleEntries(char.id, entries)}
                    />
                  ) : (
                    <CharacteristicField
                      characteristic={char}
                      value={charValue}
                      onChange={(value) => setCharValue(char.id, value)}
                      currency={charCurrency}
                      onCurrencyChange={
                        char.data_type === "currency" ? (c) => setCharCurrency(char.id, c) : undefined
                      }
                      error={showError}
                      helperText={showError ? "This field is required" : undefined}
                      currencyError={showCurrencyError}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

      <NavigationButtons
        onBack={goBack}
        onNext={() => goToScreen("summary")}
        nextLabel="Next"
        nextDisabled={submitting || !state.dealName.trim() || !state.assetClass}
      />
    </>
  );
};
